/* =========================================================================== *\
___________       __         .__
\_   _____/ _____/  |________|__| ____
 |    __)_ /    \   __\_  __ \  |/  _ \
 |        \   |  \  |  |  | \/  (  <_> )
/_______  /___|  /__|  |__|  |__|\____/
        \/     \/
\* =========================================================================== */

/*
 * หน้า "งาน AI ของฉัน" (ตรวจการใช้งาน 2.1 · 1 พ.ย. 2569)
 *   รวมภาพจากทุกหน้าจอ AI ไว้ที่เดียว: ภาพสินค้า AI · กราฟิกแคมเปญ · คิวงานภาพ · ภาพบนป้าย · ปรับสเกลภาพ
 *   ค่าตั้งต้น = "รอจัดการ" ของตัวเอง · ผู้ดูแล AI สลับดูของทุกคนได้ (หัวหน้าตรวจงานทั้งร้านในหน้าเดียว)
 *   ปุ่ม เก็บ/ผ่าน/ไม่ใช้/ลบ เรียก endpoint เดิมของแต่ละหน้า (สิทธิ์และเงื่อนไขเดิม) — หน้านี้ไม่มีกติกาของตัวเอง
 *   OPTIONS /ai_work/list  (controllers/ai_work.con.php)
 */

import React, { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import Switch from '@mui/material/Switch';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Pagination from '@mui/material/Pagination';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDSnackbar from 'components/MDSnackbar';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import Footer from 'examples/Footer';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';
import ImageReview from 'layouts/_entrio/ImageReview';
import { jobErrorText } from 'layouts/_entrio/jobCreditText';
import { tagArtErrorText } from 'layouts/tag/TagArtPanel';

const KINDS = {
  product: { label: 'ภาพสินค้า AI', icon: 'auto_awesome', keep: 'เก็บเข้าแกลเลอรี' },
  campaign: { label: 'กราฟิกแคมเปญ', icon: 'campaign', keep: 'เก็บเข้ารูปแคมเปญ' },
  job: { label: 'คิวงานภาพ', icon: 'layers', keep: 'เก็บเข้าแกลเลอรี' },
  tag_art: { label: 'ภาพบนป้าย', icon: 'sell', keep: 'ผ่าน' },
  scale: { label: 'ปรับสเกลภาพ', icon: 'aspect_ratio', keep: '' },
};
const STATES = [
  ['pending', 'รอจัดการ', 'info'],
  ['working', 'กำลังทำ', 'warning'],
  ['failed', 'ไม่สำเร็จ', 'error'],
  ['done', 'เก็บแล้ว', 'success'],
  ['rejected', 'ไม่ใช้', 'secondary'],
];
const STATE_LABEL = Object.fromEntries(STATES.map(([k, l, c]) => [k, { l, c }]));
const keyOf = (r) => `${r.kind}:${r.id}`;
/* ไม่ใช้/ลบ ของแต่ละชนิด — เส้นทางเดิมของหน้านั้น */
const DROP = { product: 'ลบ', campaign: 'ลบ', job: 'ไม่ใช้', tag_art: 'ไม่ผ่าน' };

function AiWork() {
  const app = useSelector((state) => state[APP_STATE]);
  const { t } = useTranslation();
  const [find, setFind] = useState({
    scope: 'mine',
    kind: '',
    state: 'pending',
    search: '',
    page: 1,
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState({}); // key → row (เลือกข้ามหน้าได้)
  const [reviewId, setReviewId] = useState(null);
  const [ask, setAsk] = useState(null); // {kind:'keep'|'drop', rows}
  const [notify, setNotify] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const alive = useRef(true);
  const findRef = useRef(find); // ตัวกรองล่าสุด — โหลดหลังทำรายการต้องไม่ใช้ค่าเก่าตอนเริ่มกด
  const reqSeq = useRef(0);
  const toastTimer = useRef(null);
  const searchTimer = useRef(null);

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }} // มุมล่างขวาบังปุ่มปิดหน้าต่างตรวจภาพ
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        open
        onClose={() => setNotify(null)}
        close={() => setNotify(null)}
        bgWhite
      />
    );
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(
      () => {
        if (alive.current) setNotify(null);
      },
      color === 'error' ? 8000 : 3000
    );
  };

  const load = (f = findRef.current, quiet = false) => {
    reqSeq.current += 1;
    const seq = reqSeq.current;
    if (!quiet) setLoading(true);
    FetchData('OPTIONS', '/ai_work/list', app.token, { ...f, limit: 24, _t: Date.now() })
      .then((r) => {
        if (!alive.current || seq !== reqSeq.current) return; // คำตอบของตัวกรองเดิมมาช้า ไม่ทับ
        setLoading(false);
        if (r && r.status === 'ok') {
          /* เก็บภาพหน้าสุดท้ายหมดแล้ว = ถอยไปหน้าที่ยังมีของ (เดิมขึ้น "ไม่มีภาพ" ทั้งที่ปุ่มสถานะบอกว่ามี) */
          const last = Math.ceil(Number(r.total || 0) / Number(r.limit || 24));
          if (!(r.rows || []).length && f.page > 1 && last >= 1 && f.page > last) {
            const back = { ...f, page: last };
            findRef.current = back;
            setFind(back);
            load(back, quiet);
            return;
          }
          setData(r);
          setLoadFailed(false);
        } else if (!quiet) {
          setLoadFailed(true);
          showNotify(t(`alert.loaderror`), 'error');
        }
      })
      .catch(() => {
        if (!alive.current || seq !== reqSeq.current) return;
        setLoading(false);
        if (!quiet) {
          setLoadFailed(true);
          showNotify(t(`alert.loaderror`), 'error');
        }
      });
  };

  useEffect(() => {
    alive.current = true;
    load();
    return () => {
      alive.current = false;
      clearTimeout(toastTimer.current);
      clearTimeout(searchTimer.current);
    };
  }, []);

  const working = Number((data && data.counts && data.counts.working) || 0);
  /* มีงานกำลังทำ = โหลดสถานะใหม่เป็นระยะ (เว้น ≥ 6 วิ ตาม preventDuplicate) */
  useEffect(() => {
    if (!working || busy) return undefined;
    const timer = setInterval(() => load(findRef.current, true), 8000);
    return () => clearInterval(timer);
  }, [working, busy]);

  const change = (patch) => {
    const next = { ...find, ...patch, page: patch.page || 1 };
    findRef.current = next;
    setFind(next);
    /* เปลี่ยนตัวกรอง = ล้างที่เลือก กันเก็บ/ลบภาพที่มองไม่เห็นแล้ว · เปลี่ยนหน้าเก็บไว้ (เลือกข้ามหน้าได้) */
    if (patch.page === undefined) setPicked({});
    clearTimeout(searchTimer.current);
    if (patch.search !== undefined) searchTimer.current = setTimeout(() => load(next), 400);
    else load(next);
  };

  const rows = (data && data.rows) || [];
  const counts = (data && data.counts) || {};
  const canGen = Boolean(data && data.can_generate === 'Y');
  const scopeAll = Boolean(data && data.scope === 'all');
  /* ทำได้เฉพาะงานของตัวเอง หรือผู้ดูแลที่เปิดดูของทุกคน (server เช็คซ้ำ) */
  const actionable = (r) =>
    canGen && r.state === 'pending' && r.kind !== 'scale' && (r.mine === 'Y' || scopeAll);
  const pickedRows = Object.values(picked);

  /* ยิงทีละรายการตามลำดับ (endpoint เดิมเป็นทีละภาพ · ภาพบนป้ายส่งเป็นชุดเดียว) */
  const runRows = (list, mode) => {
    setAsk(null);
    setBusy(true);
    let ok = 0;
    let fail = 0;
    let lastErr = '';
    const done = []; // แถวที่สำเร็จ — ตัดออกจากที่เลือก ส่วนที่ล้มยังเลือกไว้ให้กดซ้ำ
    const art = list.filter((r) => r.kind === 'tag_art');
    const rest = list.filter((r) => r.kind !== 'tag_art');
    const call = (r) => {
      if (mode === 'keep') {
        const path = {
          product: 'product_ai_image',
          campaign: 'campaign_graphic',
          job: 'image_job',
        }[r.kind];
        return FetchData('OPTIONS', `/${path}/approve`, app.token, { id: r.id, _t: Date.now() });
      }
      if (r.kind === 'job')
        return FetchData('OPTIONS', '/image_job/discard', app.token, { id: r.id, _t: Date.now() });
      if (r.kind === 'tag_art')
        return FetchData('OPTIONS', '/tag_art/reject', app.token, {
          id: r.id,
          note: '',
          redo: 'N',
          _t: Date.now(),
        });
      const path = r.kind === 'product' ? 'product_ai_image' : 'campaign_graphic';
      return FetchData('DELETE', `/${path}/${r.id}`, app.token, {});
    };
    const tally = (r, row) => {
      if (r && r.status === 'ok') {
        ok += 1;
        done.push(keyOf(row));
      } else {
        fail += 1;
        lastErr = (r && r.msg) || lastErr;
      }
    };
    let chain = Promise.resolve();
    if (mode === 'keep' && art.length)
      chain = chain.then(() =>
        FetchData('OPTIONS', '/tag_art/approve', app.token, {
          ids: art.map((r) => r.id),
          _t: Date.now(),
        })
          .then((r) => {
            if (r && r.status === 'ok') {
              ok += Number(r.approved || 0);
              fail += Number(r.skipped || 0);
              if (!Number(r.skipped || 0)) art.forEach((a) => done.push(keyOf(a)));
            } else {
              fail += art.length;
              lastErr = (r && r.msg) || lastErr;
            }
          })
          .catch(() => {
            fail += art.length;
          })
      );
    const each = mode === 'keep' ? rest : list;
    each.forEach((r) => {
      chain = chain.then(() =>
        call(r)
          .then((res) => tally(res, r))
          .catch(() => tally(null, r))
      );
    });
    chain.then(() => {
      if (!alive.current) return;
      setBusy(false);
      setPicked((cur) => {
        const next = { ...cur };
        done.forEach((k) => delete next[k]);
        return next;
      });
      setReviewId(null);
      const verb = mode === 'keep' ? 'เก็บ/ผ่าน' : 'ไม่ใช้/ลบ';
      showNotify(
        `${verb}แล้ว ${ok} ภาพ${
          fail
            ? ` · ไม่สำเร็จ ${fail}${
                lastErr ? ` (${jobErrorText(lastErr) || tagArtErrorText(lastErr)})` : ''
              }`
            : ''
        }`,
        fail ? 'warning' : 'success'
      );
      load();
    });
  };

  const openLink = (r) => {
    if (r.kind === 'product') return `/product/${r.ref_id}`;
    if (r.kind === 'campaign') return `/campaign/${r.ref_id}`;
    if (r.kind === 'job') return '/image_job_queue';
    if (r.kind === 'scale') return '/image_scale';
    return '';
  };

  const togglePick = (r) =>
    setPicked((cur) => {
      const next = { ...cur };
      if (next[keyOf(r)]) delete next[keyOf(r)];
      else next[keyOf(r)] = r;
      return next;
    });
  const pageActionable = rows.filter(actionable);
  const allPicked = pageActionable.length > 0 && pageActionable.every((r) => picked[keyOf(r)]);

  const reviewItems = rows
    .filter((r) => r.image_url)
    .map((r) => ({
      id: keyOf(r),
      title: `${KINDS[r.kind].label} · ${r.ref_code || `งาน #${r.id}`}`,
      src: r.image_url,
      fileName: `${r.ref_code || r.kind}_${r.id}`,
      approved: r.state === 'done',
      canApprove: actionable(r),
      approveLabel: KINDS[r.kind].keep,
      row: { id: keyOf(r) },
    }));

  const card = (r) => {
    const base = STATE_LABEL[r.state] || { l: r.state, c: 'default' };
    const st = r.kind === 'scale' && r.state === 'done' ? { ...base, l: 'เสร็จแล้ว' } : base; // ปรับสเกลไม่มีขั้นเก็บ
    const can = actionable(r);
    const link = openLink(r);
    return (
      <Grid item xs={12} sm={6} md={4} lg={3} key={keyOf(r)}>
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <MDBox
            position='relative'
            sx={{
              aspectRatio: '1 / 1',
              bgcolor: '#f3f4f7',
              cursor: r.image_url ? 'zoom-in' : 'default',
            }}
            onClick={() => r.image_url && setReviewId(keyOf(r))}
            role={r.image_url ? 'button' : undefined}
            tabIndex={r.image_url ? 0 : undefined}
            onKeyDown={(e) => {
              if (r.image_url && e.target === e.currentTarget && ['Enter', ' '].includes(e.key)) {
                e.preventDefault();
                setReviewId(keyOf(r));
              }
            }}
            aria-label={r.image_url ? `ดูภาพ ${r.ref_code || r.id}` : undefined}
          >
            {r.image_url ? (
              <img
                src={r.image_url}
                alt=''
                loading='lazy'
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            ) : (
              <MDBox height='100%' display='flex' alignItems='center' justifyContent='center'>
                {r.state === 'working' ? (
                  <CircularProgress size={28} />
                ) : (
                  <Icon sx={{ fontSize: 36, opacity: 0.3 }}>image_not_supported</Icon>
                )}
              </MDBox>
            )}
            {can ? (
              <Checkbox
                checked={Boolean(picked[keyOf(r)])}
                onClick={(e) => e.stopPropagation()}
                onChange={() => togglePick(r)}
                inputProps={{ 'aria-label': `เลือก ${r.ref_code || r.id}` }}
                sx={{
                  position: 'absolute',
                  top: 4,
                  left: 4,
                  bgcolor: 'rgba(255,255,255,.85)',
                  borderRadius: 1,
                }}
              />
            ) : null}
            <Chip
              size='small'
              color={st.c}
              label={st.l}
              sx={{ position: 'absolute', top: 8, right: 8 }}
            />
          </MDBox>
          <MDBox p={1.5} display='flex' flexDirection='column' gap={0.5} flexGrow={1}>
            <MDTypography
              variant='caption'
              color='text'
              display='flex'
              alignItems='center'
              gap={0.5}
            >
              <Icon fontSize='inherit'>{KINDS[r.kind].icon}</Icon>
              {KINDS[r.kind].label}
            </MDTypography>
            <MDTypography variant='button' fontWeight='medium' lineHeight={1.3} title={r.ref_name}>
              {r.ref_code || `งาน #${r.id}`}
              {r.ref_name ? (
                <MDTypography
                  component='span'
                  variant='caption'
                  color='text'
                  display='block'
                  noWrap
                >
                  {r.ref_name}
                </MDTypography>
              ) : null}
            </MDTypography>
            <MDTypography variant='caption' color='text' sx={{ opacity: 0.75 }}>
              {Func.dateTimeDisplay(r.created_at)}
              {scopeAll && r.created_by_name ? ` · ${r.created_by_name}` : ''}
            </MDTypography>
            {r.state === 'failed' && r.error_msg ? (
              <MDTypography variant='caption' color='error' title={r.error_msg}>
                {jobErrorText(r.error_msg) || 'ไม่สำเร็จ'}
              </MDTypography>
            ) : null}
            <MDBox mt='auto' pt={1} display='flex' gap={1} alignItems='center' flexWrap='wrap'>
              {can ? (
                <MDButton
                  size='small'
                  variant='gradient'
                  color={r.kind === 'tag_art' ? 'success' : 'info'}
                  disabled={busy}
                  onClick={() => runRows([r], 'keep')}
                  sx={{ flexGrow: 1 }}
                >
                  {KINDS[r.kind].keep}
                </MDButton>
              ) : null}
              {can ? (
                <MDButton
                  size='small'
                  variant='outlined'
                  color='error'
                  disabled={busy}
                  onClick={() => setAsk({ kind: 'drop', rows: [r] })}
                >
                  {DROP[r.kind]}
                </MDButton>
              ) : null}
              {link ? (
                <NavLink to={link}>
                  <MDButton size='small' variant='text' color='info' sx={{ px: 1 }}>
                    เปิดหน้า
                  </MDButton>
                </NavLink>
              ) : null}
            </MDBox>
          </MDBox>
        </Card>
      </Grid>
    );
  };

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.product`), 'งาน AI ของฉัน']} />
      <MDBox py={2}>
        <Card sx={{ p: 2, mb: 2 }}>
          <MDBox display='flex' alignItems='center' gap={2} flexWrap='wrap'>
            <MDTypography variant='h5' sx={{ flex: '1 1 260px' }}>
              {scopeAll ? 'งาน AI ของทุกคน' : 'งาน AI ของฉัน'}
              <MDTypography variant='caption' color='text' display='block'>
                ภาพจากทุกหน้าจอ AI รวมที่เดียว · เก็บ ผ่าน หรือไม่ใช้ได้จากหน้านี้
              </MDTypography>
            </MDTypography>
            {data && data.can_view_all === 'Y' ? (
              <FormControlLabel
                control={
                  <Switch
                    checked={find.scope === 'all'}
                    onChange={(e) => change({ scope: e.target.checked ? 'all' : 'mine' })}
                  />
                }
                label='ดูของทุกคน'
              />
            ) : null}
          </MDBox>
          <MDBox display='flex' gap={1} flexWrap='wrap' mt={2}>
            {STATES.map(([k, label]) => (
              <MDButton
                key={k}
                size='small'
                variant={find.state === k ? 'gradient' : 'outlined'}
                color='dark'
                onClick={() => change({ state: k })}
              >
                {label} ({Func.numberDisplay(counts[k] || 0, 0)})
              </MDButton>
            ))}
            <MDButton
              size='small'
              variant={find.state === '' ? 'gradient' : 'outlined'}
              color='dark'
              onClick={() => change({ state: '' })}
            >
              ทั้งหมด
            </MDButton>
          </MDBox>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12} sm={6} md={4}>
              <MDInput
                select
                fullWidth
                variant='standard'
                label='หน้าจอ'
                value={find.kind}
                onChange={(e) => change({ kind: e.target.value })}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value=''>ทุกหน้าจอ</MenuItem>
                {Object.entries(KINDS).map(([k, v]) => (
                  <MenuItem key={k} value={k}>
                    {v.label}
                  </MenuItem>
                ))}
              </MDInput>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <MDInput
                fullWidth
                variant='standard'
                label='ค้นหา'
                placeholder='รหัสสินค้า · รหัสแคมเปญ · ชื่อ'
                value={find.search}
                type='search'
                onChange={(e) => change({ search: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Card>

        {pageActionable.length ? (
          <MDBox display='flex' alignItems='center' gap={1} flexWrap='wrap' mb={1.5}>
            <FormControlLabel
              sx={{ mr: 1 }}
              control={
                <Checkbox
                  checked={allPicked}
                  onChange={() =>
                    setPicked((cur) => {
                      const next = { ...cur };
                      pageActionable.forEach((r) => {
                        if (allPicked) delete next[keyOf(r)];
                        else next[keyOf(r)] = r;
                      });
                      return next;
                    })
                  }
                />
              }
              label='เลือกทั้งหน้า'
            />
            <MDButton
              size='small'
              color='info'
              disabled={busy || !pickedRows.length}
              onClick={() => setAsk({ kind: 'keep', rows: pickedRows })}
            >
              <Icon>done_all</Icon>&nbsp;เก็บ/ผ่านที่เลือก ({pickedRows.length})
            </MDButton>
            <MDButton
              size='small'
              variant='outlined'
              color='error'
              disabled={busy || !pickedRows.length}
              onClick={() => setAsk({ kind: 'drop', rows: pickedRows })}
            >
              ไม่ใช้/ลบที่เลือก ({pickedRows.length})
            </MDButton>
            {busy ? <CircularProgress size={20} /> : null}
          </MDBox>
        ) : null}

        {loading && !data ? (
          <MDBox py={6} display='flex' justifyContent='center'>
            <CircularProgress />
          </MDBox>
        ) : (
          <Grid container spacing={2} sx={{ opacity: loading ? 0.6 : 1 }}>
            {rows.map(card)}
            {!rows.length || loadFailed ? (
              <Grid item xs={12}>
                <MDBox
                  py={loadFailed ? 2 : 6}
                  display='flex'
                  flexDirection='column'
                  alignItems='center'
                  gap={1}
                  sx={{ border: '1px dashed', borderColor: 'grey.400', borderRadius: 2 }}
                >
                  <Icon sx={{ fontSize: 36, opacity: 0.3 }}>
                    {loadFailed ? 'sync_problem' : 'inbox'}
                  </Icon>
                  <MDTypography variant='body2' color='text' sx={{ opacity: 0.7 }}>
                    {loadFailed
                      ? 'โหลดรายการไม่สำเร็จ'
                      : find.state === 'pending'
                      ? 'ไม่มีภาพรอจัดการ'
                      : 'ไม่มีงานตามตัวกรองนี้'}
                  </MDTypography>
                  {loadFailed ? (
                    <MDButton size='small' variant='outlined' color='info' onClick={() => load()}>
                      ลองใหม่
                    </MDButton>
                  ) : null}
                </MDBox>
              </Grid>
            ) : null}
          </Grid>
        )}
        {data && data.total > data.limit ? (
          <MDBox display='flex' justifyContent='center' mt={2}>
            <Pagination
              count={Math.ceil(data.total / data.limit)}
              page={find.page}
              onChange={(e, p) => change({ page: p })}
              variant='outlined'
              color='secondary'
            />
          </MDBox>
        ) : null}
      </MDBox>

      <Dialog
        open={Boolean(ask)}
        onClose={busy ? undefined : () => setAsk(null)}
        maxWidth='xs'
        fullWidth
      >
        {ask ? (
          <>
            <DialogTitle>
              {ask.kind === 'keep'
                ? `เก็บ/ผ่าน ${ask.rows.length} ภาพ?`
                : `ไม่ใช้/ลบ ${ask.rows.length} ภาพ?`}
            </DialogTitle>
            <DialogContent>
              <MDTypography variant='body2' color='text'>
                {ask.kind === 'keep'
                  ? 'ภาพสินค้าเข้าแกลเลอรีสินค้า · กราฟิกเข้ารูปแคมเปญ · ภาพบนป้ายผ่านใช้ทุกป้ายทุกสาขา'
                  : 'ภาพสินค้า/กราฟิกถูกลบ · งานคิวไม่นับรอตรวจรับ · ภาพบนป้ายไม่ผ่าน (ไม่ทำใหม่ ไม่เสียเครดิต)'}
              </MDTypography>
            </DialogContent>
            <DialogActions>
              <MDButton variant='text' color='dark' autoFocus onClick={() => setAsk(null)}>
                กลับ
              </MDButton>
              <MDButton
                color={ask.kind === 'keep' ? 'info' : 'error'}
                onClick={() => runRows(ask.rows, ask.kind)}
              >
                {ask.kind === 'keep' ? 'เก็บ/ผ่าน' : 'ไม่ใช้/ลบ'}
              </MDButton>
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      <ImageReview
        items={reviewItems}
        selectedId={reviewId}
        onChange={setReviewId}
        onClose={() => setReviewId(null)}
        busy={busy}
        onApprove={(row) => {
          const r = rows.find((x) => keyOf(x) === row.id);
          if (r) runRows([r], 'keep');
        }}
      />
      {notify}
      <Footer />
    </DashboardLayout>
  );
}

export default AiWork;
